import z from 'zod';
import { zodDecimal } from './decimal.types.js';
import { baseTransactionAccountMovement } from './transactions.types.js';
export const baseBankReconciliationEntry = {
    include: {
        associatedTransactionAccountMovement: {
            include: {
                ...baseTransactionAccountMovement.include,
                transaction: true,
            },
        },
    },
};
export const bankReconciliationEntryTransactionTypeSchema = z.enum(['debit', 'credit']);
export const bankReconciliationWithRelations = {
    include: {
        commitee: true,
        entries: baseBankReconciliationEntry,
        account: {
            select: {
                id: true,
                code: true,
                name: true,
            },
        },
    },
};
export const bankReconciliationMovement = {
    select: {
        id: true,
        amount: true,
        transactionType: true,
        financialAccountId: true,
        financialAccount: {
            select: {
                id: true,
                code: true,
                name: true,
            },
        },
        transaction: {
            select: {
                id: true,
                date: true,
                reference: true,
                description: true,
            },
        },
    },
};
export const bankReconciliationEntryDataSchema = z.object({
    transactionType: bankReconciliationEntryTransactionTypeSchema,
    amount: zodDecimal(),
    name: z.string().min(1, 'Le nom est requis'),
    date: z.coerce.date(),
    reference: z.string().min(1, 'La référence est requise'),
});
export const bankReconciliationEntryCSVSchema = z
    .object({
    Date: z.coerce.date(),
    Libellé: z.string().min(1, 'Le nom est requis'),
    Référence: z.string().min(1, 'La référence est requise'),
    Débit: zodDecimal(),
    Crédit: zodDecimal(),
    Solde: zodDecimal(),
})
    .refine(({ Débit, Crédit }) => {
    const areAmountsNonNegative = Débit.gte(0) && Crédit.gte(0);
    const isOneAmountPositive = Débit.eq(0) || Crédit.eq(0);
    return areAmountsNonNegative && isOneAmountPositive;
}, {
    message: 'Chaque ligne doit contenir soit un débit strictement positif, soit un crédit strictement positif.',
});
export const bankReconciliationEntrySchema = z.object({
    date: z.coerce.date('La date est requise'),
    name: z.string().min(1, 'Le nom est requis'),
    reference: z.string().min(1, 'La référence est requise'),
    transactionType: bankReconciliationEntryTransactionTypeSchema,
    amount: zodDecimal(),
});
export const bankReconciliationEntryCSVTransformSchema = bankReconciliationEntryCSVSchema.transform(({ Date: date, Libellé, Référence, Débit, Crédit, Solde }) => {
    const isCredit = Crédit.gt(0);
    return {
        date,
        name: Libellé,
        reference: Référence,
        transactionType: (isCredit ? 'credit' : 'debit'),
        amount: isCredit ? Crédit : Débit,
        balance: Solde,
    };
});
export const bankReconciliationDataSchema = z
    .object({
    name: z.string().min(1, 'Le nom est requis'),
    // undefined / 0 => reconcile against every bank account (code 512*); a positive id
    // scopes the reconciliation to a single bank account (and thus its bank journal).
    accountId: z.coerce.number().int().optional(),
    startDate: z.coerce
        .date()
        .optional()
        .refine((data) => data !== undefined, {
        message: 'La date de début est requise',
    }),
    endDate: z.coerce
        .date()
        .optional()
        .refine((data) => data !== undefined, {
        message: 'La date de fin est requise',
    }),
    status: z.enum(['open', 'closed']),
    // Opening / closing balance carried by the imported statement (camt.053 <Bal> OPBD/CLBD,
    // or the CSV "Solde" column). Optional: not every statement carries them.
    startBalance: zodDecimal().optional(),
    endBalance: zodDecimal().optional(),
    entries: z
        .array(bankReconciliationEntrySchema)
        .min(1, 'Le rapprochement bancaire doit contenir au moins une ligne'),
})
    .refine((data) => data.startDate && data.endDate && data.startDate <= data.endDate, {
    message: 'La date de fin doit être postérieure ou égale à la date de début',
    path: ['endDate'],
});
